/**
 * Shopify Metafield Service
 * 
 * Handles all metafield-related operations including creation, updates,
 * and formatting according to Shopify Admin API 2025-01 specifications.
 */

export class ShopifyMetafieldService {
  constructor(graphqlService) {
    this.graphqlService = graphqlService;
  }

  /**
   * Creates metafields for a product
   * @param {string} productId - The product ID
   * @param {Array} metafields - Array of metafield data
   * @returns {Promise<Array>} Created metafields
   */
  async createMetafields(productId, metafields) {
    if (!Array.isArray(metafields) || metafields.length === 0) {
      return [];
    }

    const createMetafieldsMutation = `
      mutation productCreateMetafields($productId: ID!, $metafields: [MetafieldsSetInput!]!) {
        productCreateMetafields(productId: $productId, metafields: $metafields) {
          metafields {
            id
            namespace
            key
            value
            type
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const formattedMetafields = metafields.map(metafield => ({
      namespace: this.sanitizeString(metafield.namespace),
      key: this.sanitizeString(metafield.key),
      type: metafield.type,
      value: this.formatMetafieldValue(metafield.value, metafield.type)
    }));

    const result = await this.graphqlService.mutate(createMetafieldsMutation, {
      productId,
      metafields: formattedMetafields
    });

    if (result?.productCreateMetafields?.userErrors?.length > 0) {
      throw new Error(`Failed to create metafields: ${JSON.stringify(result.productCreateMetafields.userErrors)}`);
    }

    return result?.productCreateMetafields?.metafields || [];
  }

  /**
   * Updates existing metafields
   * @param {string} productId - The product ID
   * @param {object} updates - Metafield updates mapping
   * @returns {Promise<Array>} Updated metafields
   */
  async updateMetafields(productId, updates) {
    if (!updates || Object.keys(updates).length === 0) {
      return [];
    }

    const updateMetafieldsMutation = `
      mutation productUpdateMetafields($productId: ID!, $metafields: [MetafieldsSetInput!]!) {
        productUpdateMetafields(productId: $productId, metafields: $metafields) {
          metafields {
            id
            namespace
            key
            value
            type
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const formattedUpdates = Object.values(updates).map(metafield => ({
      namespace: this.sanitizeString(metafield.namespace),
      key: this.sanitizeString(metafield.key),
      type: metafield.type,
      value: this.formatMetafieldValue(metafield.value, metafield.type)
    }));

    const result = await this.graphqlService.mutate(updateMetafieldsMutation, {
      productId,
      metafields: formattedUpdates
    });

    if (result?.productUpdateMetafields?.userErrors?.length > 0) {
      throw new Error(`Failed to update metafields: ${JSON.stringify(result.productUpdateMetafields.userErrors)}`);
    }

    return result?.productUpdateMetafields?.metafields || [];
  }

  /**
   * Deletes metafields from a product
   * @param {string} productId - The product ID
   * @param {Array<string>} metafieldIds - Array of metafield IDs to delete
   * @returns {Promise<void>}
   */
  async deleteMetafields(productId, metafieldIds) {
    if (!Array.isArray(metafieldIds) || metafieldIds.length === 0) {
      return;
    }

    const deleteMetafieldsMutation = `
      mutation productDeleteMetafields($productId: ID!, $metafieldIds: [ID!]!) {
        productDeleteMetafields(productId: $productId, metafieldIds: $metafieldIds) {
          deletedMetafieldIds
          userErrors {
            field
            message
          }
        }
      }
    `;

    const result = await this.graphqlService.mutate(deleteMetafieldsMutation, {
      productId,
      metafieldIds
    });

    if (result?.productDeleteMetafields?.userErrors?.length > 0) {
      throw new Error(`Failed to delete metafields: ${JSON.stringify(result.productDeleteMetafields.userErrors)}`);
    }
  }

  /**
   * Formats metafield values based on their type
   * @param {any} value - Raw value
   * @param {string} type - Metafield type
   * @returns {string} Formatted value
   */
  formatMetafieldValue(value, type) {
    if (value === null || value === undefined) {
      return '';
    }

    switch (type?.toLowerCase()) {
      case 'json':
      case 'json_string':
        return typeof value === 'string' ? value : JSON.stringify(value);
      case 'number_integer':
        return String(parseInt(value, 10));
      case 'number_decimal':
        return String(parseFloat(value));
      case 'boolean':
        return String(Boolean(value));
      case 'date':
      case 'date_time':
        return new Date(value).toISOString();
      case 'single_line_text_field':
      case 'multi_line_text_field':
        return this.sanitizeString(value);
      default:
        return String(value);
    }
  }

  /**
   * Extracts metafields from product data
   * @param {object} product - Product data
   * @returns {Array} Formatted metafields
   */
  extractMetafields(product) {
    const metafields = [];

    if (product.metafields) {
      if (product.metafields.edges) {
        product.metafields.edges.forEach(edge => {
          const metafield = edge.node;
          metafields.push({
            id: metafield.id,
            namespace: metafield.namespace,
            key: metafield.key,
            value: metafield.value,
            type: metafield.type
          });
        });
      } else if (Array.isArray(product.metafields)) {
        product.metafields.forEach(metafield => {
          metafields.push({
            id: metafield.id,
            namespace: metafield.namespace,
            key: metafield.key,
            value: metafield.value,
            type: metafield.type
          });
        });
      }
    }

    return metafields;
  }

  /**
   * Sanitizes a string value
   * @param {string} value - Value to sanitize
   * @returns {string} Sanitized value
   */
  sanitizeString(value) {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }
}
